import BotonInstructivo from "./BotonInstructivo";
import BotonMapaProceso from "./BotonMapaProceso";
import EscritorioComercial from "./EscritorioComercial";
import { useState, useEffect, useMemo } from "react";
import { subirDatos, subirDatosSingle, eliminarDato } from "./syncSupabase";
import { ESTADOS_OT } from "./OrdenTrabajo";

const ESTADOS = {
  prospecto:     { label: "Prospecto",     color: "#2A7FFF", bg: "#dbeafe" },
  cotizada:      { label: "Cotizada",      color: "#d97706", bg: "#fef3c7" },
  aprobada:      { label: "Aprobada",      color: "#16a34a", bg: "#dcfce7" },
  en_produccion: { label: "En producción", color: "#6B6FD6", bg: "#E8E9F6" },
  entregada:     { label: "Entregada",     color: "#374151", bg: "#F3F4F6" },
  perdida:       { label: "Perdida",       color: "#dc2626", bg: "#fee2e2" },
};

const CANALES = ["Llamada entrante","Visita comercial","Email","WhatsApp","Referido","Licitación","Web","Otro"];

function generarCod(lista) { return `VTA-${new Date().getFullYear()}-${String(lista.length+1).padStart(4,"0")}`; }
function hoy() { return new Date().toISOString().slice(0,10); }

// ─── Integración automática con Finanzas ───
// Al entregar una venta, genera la CxC + el asiento contable (Debe CxC / Haber Ventas)
// de forma idempotente (no duplica si la venta ya generó su CxC antes).
function generarCxCVenta(venta) {
  try {
    const cxcActuales = JSON.parse(localStorage.getItem('alqos_cxc_cxp') || '[]');
    if (cxcActuales.some(i => i.origenVentaId === venta.id)) return; // ya generada

    const configRaw = localStorage.getItem('alqos_finanzas_config');
    const config = configRaw ? JSON.parse(configRaw) : null;
    if (!config || !config.cuentaCxCDefault || !config.cuentaVentasDefault) return; // Finanzas sin configurar, no interrumpe Ventas

    const cuentas = JSON.parse(localStorage.getItem('alqos_plan_cuentas') || '[]');
    const cuentaCxC = cuentas.find(c => c.id === config.cuentaCxCDefault);
    const cuentaVentas = cuentas.find(c => c.id === config.cuentaVentasDefault);
    if (!cuentaCxC || !cuentaVentas) return;

    const monto = Number(venta.monto) || 0;
    if (monto <= 0) return;
    const fecha = new Date().toISOString().slice(0, 10);

    const nuevaCxC = {
      id: Math.floor(Date.now()),
      tipo: 'cobrar',
      entidadNombre: venta.cliente,
      documentoOrigen: venta.codigo,
      montoOriginal: monto,
      montoPendiente: monto,
      fechaEmision: fecha,
      fechaVencimiento: null,
      estado: 'pendiente',
      origenVentaId: venta.id,
    };
    localStorage.setItem('alqos_cxc_cxp', JSON.stringify([nuevaCxC, ...cxcActuales]));
    subirDatos('alqos_cxc_cxp');

    const asientos = JSON.parse(localStorage.getItem('alqos_asientos') || '[]');
    const lineas = JSON.parse(localStorage.getItem('alqos_asientos_lineas') || '[]');
    const nroAsiento = (Number(config.ultimoAsiento) || 0) + 1;
    const numero = `A-${new Date().getFullYear()}-${String(nroAsiento).padStart(5, '0')}`;
    const asientoId = Math.floor(Date.now()) + 1;

    const nuevoAsiento = {
      id: asientoId, numero, fecha, concepto: `Venta entregada ${venta.codigo} — ${venta.cliente}`,
      totalDebe: monto, totalHaber: monto, estado: 'confirmado',
      origenVentaId: venta.id, generadoAutomaticamente: true,
    };
    const nuevasLineas = [
      { id: Math.floor(Date.now()) + 2, asientoId, cuentaId: cuentaCxC.id, debe: monto, haber: 0 },
      { id: Math.floor(Date.now()) + 3, asientoId, cuentaId: cuentaVentas.id, debe: 0, haber: monto },
    ];
    localStorage.setItem('alqos_asientos', JSON.stringify([nuevoAsiento, ...asientos]));
    subirDatos('alqos_asientos');
    localStorage.setItem('alqos_asientos_lineas', JSON.stringify([...lineas, ...nuevasLineas]));
    subirDatos('alqos_asientos_lineas');

    // Actualiza saldos — misma convención de signos que AsientosContables.js
    const cuentasActualizadas = cuentas.map(c => {
      if (c.id === cuentaCxC.id || c.id === cuentaVentas.id) return { ...c, saldo: (Number(c.saldo) || 0) + monto };
      return c;
    });
    localStorage.setItem('alqos_plan_cuentas', JSON.stringify(cuentasActualizadas));
    subirDatos('alqos_plan_cuentas');

    const nuevaConfig = { ...config, ultimoAsiento: nroAsiento };
    localStorage.setItem('alqos_finanzas_config', JSON.stringify(nuevaConfig));
    subirDatosSingle('alqos_finanzas_config', 'finanzas_config');
  } catch (e) {
    console.error('Error generando CxC automática de venta:', e);
  }
}

const FORM_VACIO = {
  cliente:'', contacto:'', email:'', telefono:'',
  producto:'', vendedor:'', monto:'', canal:'Email',
  fecha:hoy(), fechaEntrega:'', observaciones:'',
  estado:'prospecto', cotizacionId:null, cotizacionCod:'',
};

export default function Ventas({ onVolver, oscuro, usuario }) {
  const [ventas, setVentas] = useState([]);
  const [seleccionada, setSeleccionada] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editando, setEditando] = useState(false);
  const [form, setForm] = useState(FORM_VACIO);
  const [tabDetalle, setTabDetalle] = useState('datos');
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [busqueda, setBusqueda] = useState('');
  const [vistaAnalisis, setVistaAnalisis] = useState(false);
  const [confirmEliminar, setConfirmEliminar] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [showClienteSelector, setShowClienteSelector] = useState(false);
  const [vistaEscritorio, setVistaEscritorio] = useState(false);

  const esAdmin = usuario?.rol === 'Administrador';

  useEffect(() => {
    try { setVentas(JSON.parse(localStorage.getItem('alqos_ventas') || '[]')); } catch { setVentas([]); }
    try { setClientes(JSON.parse(localStorage.getItem('alqos_clientes') || '[]')); } catch { setClientes([]); }
  }, []);

  const guardarLS = (lista) => { setVentas(lista); localStorage.setItem('alqos_ventas', JSON.stringify(lista)); subirDatos('alqos_ventas'); };
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const seleccionarCliente = (c) => {
    setForm(f => ({ ...f, cliente: c.nombre || c.razonSocial || '', contacto: c.contacto || '', email: c.email || '', telefono: c.telefono || '' }));
    setShowClienteSelector(false);
  };

  const guardar = () => {
    if (!form.cliente.trim()) return alert('El cliente es obligatorio');
    if (editando) {
      const lista = ventas.map(v => v.id===seleccionada.id ? { ...seleccionada, ...form } : v);
      guardarLS(lista);
      setSeleccionada({ ...seleccionada, ...form });
    } else {
      const nueva = { ...form, id: Date.now(), codigo: generarCod(ventas), historialEstado: [], fechaCreacion: hoy(), creadoPor: usuario?.nombre || 'Sistema' };
      guardarLS([nueva, ...ventas]);
    }
    setShowForm(false); setEditando(false); setForm(FORM_VACIO);
  };

  const cambiarEstado = (id, nuevoEstado) => {
    const entrada = { estado: nuevoEstado, fecha: hoy(), usuario: usuario?.nombre };
    const lista = ventas.map(v => v.id===id ? { ...v, estado: nuevoEstado, historialEstado: [...(v.historialEstado||[]), entrada] } : v);
    guardarLS(lista);
    if (seleccionada?.id===id) setSeleccionada(lista.find(v => v.id===id));
    if (nuevoEstado === 'entregada') {
      const ventaActualizada = lista.find(v => v.id === id);
      if (ventaActualizada) generarCxCVenta(ventaActualizada);
    }
  };

  const eliminar = async (id) => {
    const nuevas = ventas.filter(v => v.id!==id);
    setVentas(nuevas); localStorage.setItem('alqos_ventas', JSON.stringify(nuevas));
    await eliminarDato('alqos_ventas', id);
    setSeleccionada(null); setConfirmEliminar(null);
  };

  const generarPresupuesto = () => {
    if (!seleccionada) return;
    const cots = JSON.parse(localStorage.getItem('alqos_cotizaciones')||'[]');
    const yaExiste = cots.find(c=>c.ventaId===seleccionada.id);
    if (yaExiste) return alert(`Ya tiene presupuesto vinculado: ${yaExiste.codigo}`);
    const nueva = {
      id: Date.now(),
      codigo: `COT-${new Date().getFullYear()}-${String(cots.length+1).padStart(4,'0')}`,
      cliente: seleccionada.cliente,
      contacto: seleccionada.contacto||'',
      email: seleccionada.email||'',
      vendedor: seleccionada.vendedor||'',
      ventaId: seleccionada.id,
      ventaCod: seleccionada.codigo,
      items: [{descripcion: seleccionada.producto||'', cantidad:'1', precio: seleccionada.monto||'', etSku:''}],
      total: Number(seleccionada.monto||0),
      estado: 'borrador',
      version: 1,
      historialEstado: [],
      fechaCreacion: new Date().toISOString().slice(0,10),
      fecha: new Date().toISOString().slice(0,10),
      fechaVencimiento: (() => { const d=new Date(); d.setDate(d.getDate()+30); return d.toISOString().slice(0,10); })(),
      creadoPor: usuario?.nombre||'Sistema',
    };
    localStorage.setItem('alqos_cotizaciones', JSON.stringify([nueva,...cots]));
    subirDatos('alqos_cotizaciones');
    // Actualizar venta a estado cotizada
    const entrada = {estado:'cotizada', fecha:new Date().toISOString().slice(0,10), usuario:usuario?.nombre};
    const actualizadas = ventas.map(v=>v.id===seleccionada.id
      ?{...v, estado:'cotizada', cotizacionId:nueva.id, cotizacionCod:nueva.codigo, historialEstado:[...(v.historialEstado||[]),entrada]}
      :v);
    guardarLS(actualizadas);
    setSeleccionada(actualizadas.find(v=>v.id===seleccionada.id));
    alert(`✅ Presupuesto ${nueva.codigo} creado — completalo en módulo Cotizaciones`);
  };

  const getCotizacion = (v) => {
    if (!v?.cotizacionId) return null;
    try { return JSON.parse(localStorage.getItem('alqos_cotizaciones') || '[]').find(c => c.id===v.cotizacionId); } catch { return null; }
  };

  const getOTs = (v) => {
    try {
      const ots = JSON.parse(localStorage.getItem('alqos_ots')||'[]');
      return ots.filter(o => o.cotizacionId===v.cotizacionId || o.ventaId===v.id || o.cliente===v.cliente);
    } catch { return []; }
  };

  const filtradas = ventas.filter(v => {
    const matchEstado = filtroEstado==='todos' || v.estado===filtroEstado;
    const matchBusq = !busqueda || v.cliente?.toLowerCase().includes(busqueda.toLowerCase()) || v.codigo?.toLowerCase().includes(busqueda.toLowerCase());
    return matchEstado && matchBusq;
  });

  const analisis = useMemo(() => {
    if (!ventas.length) return null;
    const ahora = new Date();
    const mes = ahora.getMonth(); const anio = ahora.getFullYear();
    const mesAnt = mes===0?11:mes-1; const anioAnt = mes===0?anio-1:anio;
    const vMes = ventas.filter(v => { try { const f=new Date(v.fecha); return f.getMonth()===mes && f.getFullYear()===anio; } catch { return false; } });
    const vMesAnt = ventas.filter(v => { try { const f=new Date(v.fecha); return f.getMonth()===mesAnt && f.getFullYear()===anioAnt; } catch { return false; } });
    const montoMes = vMes.filter(v=>['aprobada','entregada'].includes(v.estado)).reduce((a,v)=>a+Number(v.monto||0),0);
    const montoMesAnt = vMesAnt.filter(v=>['aprobada','entregada'].includes(v.estado)).reduce((a,v)=>a+Number(v.monto||0),0);
    const variacion = montoMesAnt>0?Math.round(((montoMes-montoMesAnt)/montoMesAnt)*100):null;
    const porCliente = {};
    ventas.forEach(v => { if(!v.cliente) return; if(!porCliente[v.cliente]) porCliente[v.cliente]={total:0,monto:0,estados:[]}; porCliente[v.cliente].total++; porCliente[v.cliente].monto+=Number(v.monto||0); porCliente[v.cliente].estados.push(v.estado); });
    const topClientes = Object.entries(porCliente).sort((a,b)=>b[1].monto-a[1].monto).slice(0,3);
    const enRiesgo = Object.entries(porCliente).filter(([_,d])=>d.estados.every(e=>['prospecto','cotizada','perdida'].includes(e))).slice(0,3);
    const porCanal = {};
    ventas.filter(v=>['aprobada','entregada'].includes(v.estado)).forEach(v=>{ if(!v.canal) return; porCanal[v.canal]=(porCanal[v.canal]||0)+1; });
    const canalTop = Object.entries(porCanal).sort((a,b)=>b[1]-a[1])[0];
    const ganadas = ventas.filter(v=>['aprobada','entregada'].includes(v.estado)).length;
    const perdidas = ventas.filter(v=>v.estado==='perdida').length;
    const tasaConversion = ventas.length>0?Math.round((ganadas/ventas.length)*100):0;
    return { ventasMes:vMes.length, montoMes, variacion, topClientes, enRiesgo, canalTop, tasaConversion, ganadas, perdidas };
  }, [ventas]);

  const inp = { width:'100%', padding:'8px 10px', borderRadius:'8px', border:'1px solid #E5E7EB', fontSize:'13px', boxSizing:'border-box', fontFamily:'Inter, sans-serif' };
  const lbl = { fontSize:'11px', fontWeight:'600', color:'#6B7280', marginBottom:'4px', display:'block' };
  const secTit = { fontSize:'11px', fontWeight:'700', color:'#1C3FAA', margin:'14px 0 8px', paddingBottom:'5px', borderBottom:'1px solid #E8E9F6', textTransform:'uppercase', letterSpacing:'0.08em' };
  const Campo = ({ k, v }) => v ? (
    <div style={{ display:'flex', justifyContent:'space-between', fontSize:'12px', marginBottom:'6px', paddingBottom:'6px', borderBottom:'1px solid #F9FAFB' }}>
      <span style={{ color:'#6B7280' }}>{k}</span>
      <span style={{ color:'#111827', fontWeight:'500', textAlign:'right', maxWidth:'60%' }}>{v}</span>
    </div>
  ) : null;

  const montoTotal = ventas.filter(v=>['aprobada','entregada'].includes(v.estado)).reduce((a,v)=>a+Number(v.monto||0),0);

  return (
    <div style={{ padding:'24px', fontFamily:'Inter, sans-serif', background:'#E8E9F6', minHeight:'100vh' }}>

      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'20px' }}>
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
            <button onClick={onVolver} style={{ background:'none', border:'1px solid #E5E7EB', borderRadius:'8px', padding:'6px 12px', fontSize:'12px', cursor:'pointer', color:'#6B7280' }}>← Menú</button>
          <BotonInstructivo moduloKey="ventas" oscuro={oscuro} />
          {usuario?.rol === "Administrador" && <BotonMapaProceso moduloKey="ventas" oscuro={oscuro} />}
            <span style={{ fontSize:'22px' }}>📈</span>
            <h1 style={{ fontSize:'20px', fontWeight:'700', color:'#1C3FAA', margin:0 }}>Ventas</h1>
          </div>
          <p style={{ fontSize:'12px', color:'#6B7280', marginTop:'4px', marginLeft:'32px' }}>Vinculado con Cotizaciones · Clientes · OT</p>
        </div>
        <div style={{ display:'flex', gap:'8px' }}>
          <button onClick={() => { setVistaEscritorio(v=>!v); setVistaAnalisis(false); }}
            style={{ padding:'8px 14px', borderRadius:'10px', border:'1px solid #E5E7EB', background:vistaEscritorio?'#6B6FD6':'white', color:vistaEscritorio?'white':'#374151', fontSize:'12px', fontWeight:'600', cursor:'pointer' }}>
            {vistaEscritorio?'📋 Listado':'🗂️ Escritorio'}
          </button>
          <button onClick={() => { setVistaAnalisis(v=>!v); setVistaEscritorio(false); }}
            style={{ padding:'8px 14px', borderRadius:'10px', border:'1px solid #E5E7EB', background:vistaAnalisis?'#1C3FAA':'white', color:vistaAnalisis?'white':'#374151', fontSize:'12px', fontWeight:'600', cursor:'pointer' }}>
            {vistaAnalisis?'📋 Listado':'📊 Análisis'}
          </button>
          <button onClick={() => { setForm(FORM_VACIO); setEditando(false); setShowForm(true); }}
            style={{ background:'#1C3FAA', color:'white', border:'none', borderRadius:'10px', padding:'10px 18px', fontSize:'13px', fontWeight:'600', cursor:'pointer' }}>+ Nueva venta</button>
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:'10px', marginBottom:'20px' }}>
        {[
          { label:'Total', valor:ventas.length, color:'#1C3FAA' },
          { label:'Aprobadas', valor:ventas.filter(v=>v.estado==='aprobada').length, color:'#16a34a' },
          { label:'En producción', valor:ventas.filter(v=>v.estado==='en_produccion').length, color:'#6B6FD6' },
          { label:'Perdidas', valor:ventas.filter(v=>v.estado==='perdida').length, color:'#dc2626' },
          { label:'Monto total', valor:`$${montoTotal.toLocaleString('es-AR')}`, color:'#16a34a' },
        ].map((m,i) => (
          <div key={i} style={{ background:'white', borderRadius:'12px', padding:'12px 14px', border:'1px solid #E5E7EB' }}>
            <div style={{ fontSize:'11px', color:'#6B7280', marginBottom:'4px' }}>{m.label}</div>
            <div style={{ fontSize:i===4?'14px':'24px', fontWeight:'700', color:m.color }}>{m.valor}</div>
          </div>
        ))}
      </div>

      {vistaEscritorio && <EscritorioComercial oscuro={oscuro} usuario={usuario} />}

      {!vistaEscritorio && vistaAnalisis && analisis && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'20px' }}>
          <div style={{ background:'white', borderRadius:'12px', padding:'16px', border:'1px solid #E5E7EB' }}>
            <div style={secTit}>📅 Comparativa mensual</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'8px' }}>
              {[['Ventas este mes',analisis.ventasMes,'#2A7FFF'],['Monto',`$${analisis.montoMes.toLocaleString('es-AR')}`,'#16a34a'],['vs. mes ant.',analisis.variacion!==null?`${analisis.variacion>0?'+':''}${analisis.variacion}%`:'—',analisis.variacion>=0?'#16a34a':'#dc2626']].map(([l,v,c])=>(
                <div key={l} style={{ background:'#F9FAFB', borderRadius:'8px', padding:'10px', textAlign:'center' }}>
                  <div style={{ fontSize:'16px', fontWeight:'700', color:c }}>{v}</div>
                  <div style={{ fontSize:'10px', color:'#6B7280', marginTop:'2px' }}>{l}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop:'12px', display:'flex', gap:'8px' }}>
              {[['Conversión',`${analisis.tasaConversion}%`,analisis.tasaConversion>=50?'#16a34a':'#d97706'],['Ganadas',analisis.ganadas,'#16a34a'],['Perdidas',analisis.perdidas,'#dc2626']].map(([l,v,c])=>(
                <div key={l} style={{ flex:1, background:'#F9FAFB', borderRadius:'8px', padding:'8px', textAlign:'center' }}>
                  <div style={{ fontSize:'15px', fontWeight:'700', color:c }}>{v}</div>
                  <div style={{ fontSize:'10px', color:'#6B7280' }}>{l}</div>
                </div>
              ))}
            </div>
            {analisis.canalTop && (
              <div style={{ marginTop:'10px', background:'#F0FDF4', borderRadius:'8px', padding:'8px 10px', fontSize:'12px', color:'#16a34a', fontWeight:'600' }}>
                🏆 Canal top: {analisis.canalTop[0]} ({analisis.canalTop[1]} ventas)
              </div>
            )}
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
            {analisis.topClientes.length>0 && (
              <div style={{ background:'white', borderRadius:'12px', padding:'16px', border:'1px solid #E5E7EB' }}>
                <div style={secTit}>⭐ Top clientes</div>
                {analisis.topClientes.map(([cliente,data],i)=>(
                  <div key={cliente} style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:'1px solid #F3F4F6', fontSize:'12px' }}>
                    <span style={{ color:'#374151', fontWeight:'600' }}>{['🥇','🥈','🥉'][i]} {cliente}</span>
                    <span style={{ color:'#1C3FAA', fontWeight:'700' }}>${data.monto.toLocaleString('es-AR')}</span>
                  </div>
                ))}
              </div>
            )}
            {analisis.enRiesgo.length>0 && (
              <div style={{ background:'white', borderRadius:'12px', padding:'16px', border:'1px solid #FECACA' }}>
                <div style={{ ...secTit, color:'#dc2626' }}>⚠️ Sin conversión</div>
                {analisis.enRiesgo.map(([cliente,data])=>(
                  <div key={cliente} style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:'1px solid #F3F4F6', fontSize:'12px' }}>
                    <span style={{ color:'#374151' }}>{cliente}</span>
                    <span style={{ color:'#dc2626', fontWeight:'600' }}>{data.total} sin cerrar</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {!vistaEscritorio && !vistaAnalisis && (
        <div style={{ display:'flex', gap:'10px', marginBottom:'14px', alignItems:'center', flexWrap:'wrap' }}>
          <input placeholder="🔍 Buscar por cliente o código..." value={busqueda} onChange={e => setBusqueda(e.target.value)}
            style={{ ...inp, width:'260px', margin:0 }} />
          <div style={{ display:'flex', gap:'4px', background:'white', padding:'4px', borderRadius:'10px', border:'1px solid #E5E7EB' }}>
            {['todos',...Object.keys(ESTADOS)].map(k => (
              <button key={k} onClick={() => setFiltroEstado(k)}
                style={{ padding:'5px 10px', borderRadius:'7px', border:'none', fontSize:'11px', fontWeight:'500', cursor:'pointer', background:filtroEstado===k?'#1C3FAA':'transparent', color:filtroEstado===k?'white':'#6B7280' }}>
                {k==='todos'?'Todas':ESTADOS[k]?.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {!vistaEscritorio && !vistaAnalisis && (
        <div style={{ display:'grid', gridTemplateColumns:seleccionada?'1fr 400px':'1fr', gap:'14px' }}>
          <div style={{ background:'white', borderRadius:'14px', border:'1px solid #E5E7EB', overflow:'hidden' }}>
            {filtradas.length===0 ? (
              <div style={{ padding:'60px', textAlign:'center', color:'#9CA3AF' }}>
                <div style={{ fontSize:'40px', marginBottom:'12px' }}>📈</div>
                <div style={{ fontSize:'14px', fontWeight:'600' }}>Sin ventas registradas</div>
                <div style={{ fontSize:'12px', marginTop:'4px' }}>Creá la primera o aprobá una cotización</div>
              </div>
            ) : (
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr style={{ background:'#F9FAFB' }}>
                    {['Código','Cliente','Producto','Monto','Canal','Cotización','Estado'].map(h => (
                      <th key={h} style={{ padding:'10px 14px', fontSize:'11px', fontWeight:'600', color:'#6B7280', textAlign:'left', borderBottom:'1px solid #E5E7EB' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtradas.map(v => {
                    const est = ESTADOS[v.estado] || ESTADOS.prospecto;
                    return (
                      <tr key={v.id} onClick={() => { setSeleccionada(v); setTabDetalle('datos'); }}
                        style={{ cursor:'pointer', background:seleccionada?.id===v.id?'#EFF6FF':'white' }}
                        onMouseEnter={e => { if(seleccionada?.id!==v.id) e.currentTarget.style.background='#F9FAFB'; }}
                        onMouseLeave={e => { e.currentTarget.style.background=seleccionada?.id===v.id?'#EFF6FF':'white'; }}>
                        <td style={{ padding:'11px 14px', borderBottom:'1px solid #F3F4F6' }}>
                          <div style={{ fontWeight:'600', fontSize:'12px', color:'#1C3FAA' }}>{v.codigo}</div>
                          <div style={{ fontSize:'11px', color:'#9CA3AF' }}>{v.fechaCreacion}</div>
                        </td>
                        <td style={{ padding:'11px 14px', borderBottom:'1px solid #F3F4F6' }}>
                          <div style={{ fontWeight:'600', fontSize:'13px', color:'#111827' }}>{v.cliente}</div>
                          <div style={{ fontSize:'11px', color:'#9CA3AF' }}>{v.vendedor||'—'}</div>
                        </td>
                        <td style={{ padding:'11px 14px', fontSize:'12px', color:'#374151', borderBottom:'1px solid #F3F4F6', maxWidth:'140px' }}>
                          <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{v.producto||'—'}</div>
                        </td>
                        <td style={{ padding:'11px 14px', borderBottom:'1px solid #F3F4F6' }}>
                          <div style={{ fontWeight:'700', fontSize:'13px', color:'#111827' }}>{v.monto?`$${Number(v.monto).toLocaleString('es-AR')}`: '—'}</div>
                        </td>
                        <td style={{ padding:'11px 14px', fontSize:'11px', color:'#6B7280', borderBottom:'1px solid #F3F4F6' }}>{v.canal||'—'}</td>
                        <td style={{ padding:'11px 14px', borderBottom:'1px solid #F3F4F6' }}>
                          {v.cotizacionCod
                            ? <span style={{ background:'#fef3c7', color:'#d97706', fontSize:'11px', padding:'2px 8px', borderRadius:'6px', fontWeight:'600' }}>💰 {v.cotizacionCod}</span>
                            : <span style={{ color:'#D1D5DB', fontSize:'11px' }}>—</span>}
                        </td>
                        <td style={{ padding:'11px 14px', borderBottom:'1px solid #F3F4F6' }}>
                          <span style={{ background:est.bg, color:est.color, fontSize:'11px', padding:'3px 9px', borderRadius:'20px', fontWeight:'600' }}>{est.label}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {seleccionada && (
            <div style={{ background:'white', borderRadius:'14px', border:'1px solid #E5E7EB', overflow:'hidden', maxHeight:'82vh', overflowY:'auto' }}>
              <div style={{ padding:'14px 16px', borderBottom:'1px solid #F3F4F6', display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                <div>
                  <div style={{ fontSize:'12px', color:'#1C3FAA', fontWeight:'600' }}>{seleccionada.codigo}</div>
                  <div style={{ fontWeight:'700', fontSize:'15px', color:'#111827' }}>{seleccionada.cliente}</div>
                  <div style={{ marginTop:'6px', display:'flex', gap:'5px', flexWrap:'wrap' }}>
                    <span style={{ background:ESTADOS[seleccionada.estado]?.bg, color:ESTADOS[seleccionada.estado]?.color, fontSize:'11px', padding:'2px 9px', borderRadius:'20px', fontWeight:'600' }}>{ESTADOS[seleccionada.estado]?.label}</span>
                    {seleccionada.monto && <span style={{ background:'#F3F4F6', color:'#374151', fontSize:'11px', padding:'2px 8px', borderRadius:'6px', fontWeight:'700' }}>${Number(seleccionada.monto).toLocaleString('es-AR')}</span>}
                    {seleccionada.cotizacionCod && <span style={{ background:'#fef3c7', color:'#d97706', fontSize:'11px', padding:'2px 8px', borderRadius:'6px', fontWeight:'600' }}>💰 {seleccionada.cotizacionCod}</span>}
                  </div>
                </div>
                <div style={{ display:'flex', gap:'5px' }}>
                  <button onClick={() => { setForm({ ...FORM_VACIO, ...seleccionada }); setEditando(true); setShowForm(true); }}
                    style={{ background:'#1C3FAA', color:'white', border:'none', borderRadius:'8px', padding:'6px 10px', fontSize:'11px', fontWeight:'600', cursor:'pointer' }}>✏️ Editar</button>
                  {esAdmin && <button onClick={() => setConfirmEliminar(seleccionada.id)}
                    style={{ background:'#fee2e2', color:'#dc2626', border:'none', borderRadius:'8px', padding:'6px 10px', fontSize:'11px', fontWeight:'600', cursor:'pointer' }}>🗑</button>}
                  <button onClick={() => setSeleccionada(null)} style={{ background:'none', border:'none', fontSize:'18px', cursor:'pointer', color:'#9CA3AF' }}>✕</button>
                </div>
              </div>

              <div style={{ display:'flex', gap:'2px', padding:'8px 12px', borderBottom:'1px solid #F3F4F6', background:'#F9FAFB' }}>
                {[['datos','📋 Datos'],['estado','🔄 Estado'],['cotizacion','💰 Cotización'],['ots','📋 OTs'],['acciones','⚡ Acciones']].map(([id,lb])=>(
                  <button key={id} onClick={() => setTabDetalle(id)}
                    style={{ padding:'5px 10px', borderRadius:'7px', border:'none', fontSize:'11px', fontWeight:'500', cursor:'pointer', background:tabDetalle===id?'#1C3FAA':'transparent', color:tabDetalle===id?'white':'#6B7280' }}>{lb}</button>
                ))}
              </div>

              <div style={{ padding:'14px 16px' }}>
                {tabDetalle==='datos' && (<>
                  <div style={secTit}>Cliente</div>
                  <Campo k="Cliente" v={seleccionada.cliente} />
                  <Campo k="Contacto" v={seleccionada.contacto} />
                  <Campo k="Email" v={seleccionada.email} />
                  <Campo k="Teléfono" v={seleccionada.telefono} />
                  <div style={secTit}>Venta</div>
                  <Campo k="Producto / Trabajo" v={seleccionada.producto} />
                  <Campo k="Vendedor" v={seleccionada.vendedor} />
                  <Campo k="Canal" v={seleccionada.canal} />
                  <Campo k="Monto" v={seleccionada.monto?`$${Number(seleccionada.monto).toLocaleString('es-AR')}`:null} />
                  <Campo k="Fecha" v={seleccionada.fecha} />
                  <Campo k="Fecha entrega" v={seleccionada.fechaEntrega} />
                  <Campo k="Creado por" v={seleccionada.creadoPor} />
                  {seleccionada.observaciones && (
                    <div style={{ marginTop:'10px', background:'#F9FAFB', borderRadius:'8px', padding:'10px', fontSize:'12px', color:'#374151' }}>
                      <div style={{ fontWeight:'600', fontSize:'11px', color:'#6B7280', marginBottom:'4px' }}>Observaciones</div>
                      {seleccionada.observaciones}
                    </div>
                  )}
                </>)}

                {tabDetalle==='estado' && (<>
                  <div style={secTit}>Cambiar estado</div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px', marginBottom:'16px' }}>
                    {Object.entries(ESTADOS).map(([val,{label,color,bg}])=>(
                      <button key={val} onClick={() => cambiarEstado(seleccionada.id, val)}
                        style={{ padding:'10px', borderRadius:'10px', border:`1.5px solid ${seleccionada.estado===val?color:'#E5E7EB'}`, background:seleccionada.estado===val?bg:'white', color:seleccionada.estado===val?color:'#6B7280', fontSize:'12px', fontWeight:'600', cursor:'pointer' }}>
                        {label}
                      </button>
                    ))}
                  </div>
                  <div style={secTit}>Historial</div>
                  {(seleccionada.historialEstado||[]).length===0
                    ? <div style={{ fontSize:'12px', color:'#9CA3AF', textAlign:'center', padding:'20px' }}>Sin cambios registrados</div>
                    : [...(seleccionada.historialEstado||[])].reverse().map((h,i)=>(
                      <div key={i} style={{ display:'flex', alignItems:'center', gap:'8px', padding:'7px 0', borderBottom:'1px solid #F3F4F6', fontSize:'12px' }}>
                        <span style={{ background:ESTADOS[h.estado]?.bg, color:ESTADOS[h.estado]?.color, padding:'2px 8px', borderRadius:'20px', fontSize:'11px', fontWeight:'600' }}>{ESTADOS[h.estado]?.label}</span>
                        <span style={{ color:'#6B7280' }}>{h.fecha} · {h.usuario}</span>
                      </div>
                    ))
                  }
                </>)}

                {tabDetalle==='ots' && (() => {
                  const ots = getOTs(seleccionada);
                  // ESTADOS_OT importado de OrdenTrabajo.js (fuente de verdad, 6 estados
                  // reales) — antes esta pestaña mantenía su propia copia con solo 4
                  // valores, así que una OT aprobada/entregada/rechazada se mostraba
                  // siempre como "Pendiente" acá.
                  return (
                    <div>
                      <div style={{fontSize:'11px',fontWeight:'700',color:'#1C3FAA',marginBottom:'10px',paddingBottom:'5px',borderBottom:'1px solid #E8E9F6',textTransform:'uppercase',letterSpacing:'0.08em'}}>Órdenes de Trabajo vinculadas</div>
                      {ots.length===0
                        ?<div style={{textAlign:'center',padding:'30px',color:'#9CA3AF'}}>
                          <div style={{fontSize:'28px',marginBottom:'8px'}}>📋</div>
                          <div style={{fontSize:'12px'}}>Sin OTs vinculadas</div>
                          <div style={{fontSize:'11px',marginTop:'4px'}}>Se generan desde tab Acciones de la cotización</div>
                        </div>
                        :ots.map(ot=>{
                          const est = ESTADOS_OT[ot.estado]||ESTADOS_OT.pendiente;
                          return(
                            <div key={ot.id ?? ot.op} style={{background:'#F9FAFB',borderRadius:'10px',padding:'12px',marginBottom:'8px',border:'1px solid #E5E7EB'}}>
                              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'6px'}}>
                                <span style={{fontSize:'12px',fontWeight:'700',color:'#1C3FAA'}}>{ot.op || ot.numero}</span>
                                <span style={{background:est.bg,color:est.color,fontSize:'11px',padding:'2px 8px',borderRadius:'20px',fontWeight:'600'}}>{est.label}</span>
                              </div>
                              <div style={{fontSize:'12px',color:'#374151',marginBottom:'3px'}}>{ot.trabajo || ot.descripcion}</div>
                              <div style={{fontSize:'11px',color:'#9CA3AF'}}>{ot.fecha} · {ot.creadoPor}</div>
                            </div>
                          );
                        })
                      }
                    </div>
                  );
                })()}
                {tabDetalle==='acciones' && (
                  <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
                    <div style={{fontSize:'11px',fontWeight:'700',color:'#1C3FAA',marginBottom:'4px',paddingBottom:'5px',borderBottom:'1px solid #E8E9F6',textTransform:'uppercase',letterSpacing:'0.08em'}}>Acciones vinculadas</div>
                    {!seleccionada.cotizacionId&&(
                      <button onClick={generarPresupuesto} style={{padding:'12px',borderRadius:'10px',border:'1px solid #fef3c7',background:'#FFFBEB',fontSize:'12px',fontWeight:'600',cursor:'pointer',textAlign:'left',color:'#d97706'}}>
                        <div style={{fontWeight:'700',marginBottom:'2px'}}>💰 Generar presupuesto</div>
                        <div style={{fontSize:'11px',color:'#6B7280'}}>Crea una cotización vinculada a esta venta</div>
                      </button>
                    )}
                    {seleccionada.cotizacionId&&(
                      <div style={{padding:'12px',borderRadius:'10px',border:'1px solid #dcfce7',background:'#F0FDF4',fontSize:'12px',color:'#16a34a'}}>
                        <div style={{fontWeight:'700',marginBottom:'2px'}}>✅ Presupuesto vinculado</div>
                        <div style={{fontSize:'11px',color:'#6B7280'}}>{seleccionada.cotizacionCod}</div>
                      </div>
                    )}
                  </div>
                )}
                {tabDetalle==='cotizacion' && (<>
                  {seleccionada.cotizacionId ? (() => {
                    const cot = getCotizacion(seleccionada);
                    return cot ? (<>
                      <div style={{ background:'#fef3c7', borderRadius:'10px', padding:'12px', marginBottom:'12px', border:'1px solid #fde68a' }}>
                        <div style={{ fontSize:'12px', fontWeight:'700', color:'#d97706', marginBottom:'4px' }}>💰 {cot.codigo}</div>
                        <div style={{ fontSize:'13px', fontWeight:'600', color:'#111827' }}>${Number(cot.total||0).toLocaleString('es-AR')}</div>
                        <div style={{ fontSize:'11px', color:'#6B7280', marginTop:'2px' }}>{cot.fechaCreacion} · {cot.items?.length||0} ítems</div>
                      </div>
                      {(cot.items||[]).map((item,i)=>(
                        <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:'1px solid #F3F4F6', fontSize:'12px' }}>
                          <span style={{ color:'#374151' }}>{item.descripcion}</span>
                          <span style={{ color:'#1C3FAA', fontWeight:'600' }}>${((Number(item.cantidad)||0)*(Number(item.precio)||0)).toLocaleString('es-AR')}</span>
                        </div>
                      ))}
                    </>) : <div style={{ fontSize:'12px', color:'#9CA3AF', textAlign:'center', padding:'20px' }}>Cotización no encontrada</div>;
                  })() : (
                    <div style={{ textAlign:'center', padding:'30px', color:'#9CA3AF' }}>
                      <div style={{ fontSize:'30px', marginBottom:'8px' }}>💰</div>
                      <div style={{ fontSize:'12px' }}>Sin cotización vinculada</div>
                      <div style={{ fontSize:'11px', marginTop:'4px' }}>Se vincula automáticamente desde Cotizaciones</div>
                    </div>
                  )}
                </>)}
              </div>
            </div>
          )}
        </div>
      )}

      {showForm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div style={{ background:'white', borderRadius:'16px', width:'560px', maxHeight:'92vh', overflowY:'auto', padding:'24px', boxShadow:'0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
              <h2 style={{ fontSize:'16px', fontWeight:'700', color:'#1C3FAA', margin:0 }}>{editando?'✏️ Editar venta':'📈 Nueva venta'}</h2>
              <button onClick={()=>{ setShowForm(false); setEditando(false); }} style={{ background:'none', border:'none', fontSize:'20px', cursor:'pointer', color:'#9CA3AF' }}>✕</button>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
              <div style={{ gridColumn:'1/-1' }}>
                <label style={lbl}>Cliente *</label>
                <div style={{ display:'flex', gap:'8px' }}>
                  <input style={{ ...inp, flex:1 }} placeholder="Nombre del cliente" value={form.cliente} onChange={e => set('cliente', e.target.value)} />
                  <button onClick={() => setShowClienteSelector(s=>!s)}
                    style={{ padding:'8px 12px', borderRadius:'8px', border:'1px solid #E5E7EB', background:'#F9FAFB', fontSize:'12px', cursor:'pointer', color:'#1C3FAA', fontWeight:'600', whiteSpace:'nowrap' }}>
                    📋 Módulo
                  </button>
                </div>
                {showClienteSelector && clientes.length>0 && (
                  <div style={{ background:'white', border:'1px solid #E5E7EB', borderRadius:'10px', marginTop:'6px', maxHeight:'140px', overflowY:'auto', boxShadow:'0 4px 16px rgba(0,0,0,0.1)' }}>
                    {clientes.map(c=>(
                      <div key={c.id} onClick={() => seleccionarCliente(c)}
                        style={{ padding:'10px 14px', cursor:'pointer', fontSize:'12px', borderBottom:'1px solid #F3F4F6' }}
                        onMouseEnter={e=>e.currentTarget.style.background='#F9FAFB'}
                        onMouseLeave={e=>e.currentTarget.style.background='white'}>
                        <div style={{ fontWeight:'600' }}>{c.nombre||c.razonSocial}</div>
                        <div style={{ fontSize:'11px', color:'#9CA3AF' }}>{c.email}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div><label style={lbl}>Producto / Trabajo</label><input style={inp} placeholder="Descripción" value={form.producto} onChange={e=>set('producto',e.target.value)} /></div>
              <div><label style={lbl}>Vendedor</label><input style={inp} placeholder="Nombre" value={form.vendedor} onChange={e=>set('vendedor',e.target.value)} /></div>
              <div><label style={lbl}>Monto ($)</label><input type="number" style={inp} placeholder="0" value={form.monto} onChange={e=>set('monto',e.target.value)} /></div>
              <div><label style={lbl}>Canal</label>
                <select style={inp} value={form.canal} onChange={e=>set('canal',e.target.value)}>{CANALES.map(c=><option key={c}>{c}</option>)}</select>
              </div>
              <div><label style={lbl}>Fecha</label><input type="date" style={inp} value={form.fecha} onChange={e=>set('fecha',e.target.value)} /></div>
              <div><label style={lbl}>Fecha entrega</label><input type="date" style={inp} value={form.fechaEntrega} onChange={e=>set('fechaEntrega',e.target.value)} /></div>
              <div style={{ gridColumn:'1/-1' }}>
                <label style={lbl}>Estado</label>
                <div style={{ display:'flex', gap:'6px', flexWrap:'wrap', marginTop:'4px' }}>
                  {Object.entries(ESTADOS).map(([val,{label,color,bg}])=>(
                    <button key={val} onClick={()=>set('estado',val)}
                      style={{ padding:'6px 12px', borderRadius:'8px', border:`1.5px solid ${form.estado===val?color:'#E5E7EB'}`, background:form.estado===val?bg:'white', color:form.estado===val?color:'#6B7280', fontSize:'12px', fontWeight:'600', cursor:'pointer' }}>{label}</button>
                  ))}
                </div>
              </div>
              <div style={{ gridColumn:'1/-1' }}>
                <label style={lbl}>Observaciones</label>
                <textarea style={{ ...inp, minHeight:'60px', resize:'vertical' }} value={form.observaciones} onChange={e=>set('observaciones',e.target.value)} />
              </div>
            </div>
            <div style={{ display:'flex', gap:'10px', marginTop:'20px' }}>
              <button onClick={()=>{ setShowForm(false); setEditando(false); }} style={{ flex:1, padding:'10px', borderRadius:'10px', border:'1px solid #E5E7EB', background:'white', fontSize:'13px', fontWeight:'600', cursor:'pointer', color:'#6B7280' }}>Cancelar</button>
              <button onClick={guardar} style={{ flex:2, padding:'10px', borderRadius:'10px', border:'none', background:'#1C3FAA', color:'white', fontSize:'13px', fontWeight:'600', cursor:'pointer' }}>Guardar venta</button>
            </div>
          </div>
        </div>
      )}

      {confirmEliminar && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1001 }}>
          <div style={{ background:'white', borderRadius:'16px', padding:'28px', maxWidth:'340px', width:'90%', textAlign:'center' }}>
            <div style={{ fontSize:'36px', marginBottom:'12px' }}>📈</div>
            <div style={{ fontSize:'15px', fontWeight:'600', color:'#111827', marginBottom:'8px' }}>¿Eliminar esta venta?</div>
            <div style={{ fontSize:'12px', color:'#6B7280', marginBottom:'20px' }}>Esta acción no se puede deshacer.</div>
            <div style={{ display:'flex', gap:'10px' }}>
              <button onClick={()=>setConfirmEliminar(null)} style={{ flex:1, padding:'10px', borderRadius:'10px', border:'1px solid #E5E7EB', background:'white', fontSize:'13px', fontWeight:'600', cursor:'pointer', color:'#6B7280' }}>Cancelar</button>
              <button onClick={()=>eliminar(confirmEliminar)} style={{ flex:1, padding:'10px', borderRadius:'10px', border:'none', background:'#dc2626', color:'white', fontSize:'13px', fontWeight:'600', cursor:'pointer' }}>Eliminar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
